import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVertOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturnOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardLineChart from '@/components/dashboard/DashboardLineChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { groupCountBy, groupSumByMonth } from '@/lib/dashboardAggregations';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { withId } from '@/pages/reports/reportHelpers';
import type { CycleCountVarianceRow, ReturnNoteRegisterRow, StockAdjustmentRegisterRow } from '@/types/reports';

const STATUS_SLICE_COLORS = ['#0566d9', '#ffb951', '#4edea3', '#ba1a1a', '#6b6f76', '#3e5c8a'];

export default function AdjustmentsPanel() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const { formatDate } = useDateFormat();
  const colors = useCategoryColors();
  const { data: processConfiguration } = useProcessConfiguration();
  // Each of this panel's 3 sub-sections tracks its own independently-toggleable master switch —
  // same treatment as the left nav (AppShell.tsx) for these exact 3 screens.
  const stockAdjustmentEnabled = processConfiguration ? processConfiguration.stockAdjustmentEnabled : true;
  const cycleCountEnabled = processConfiguration ? processConfiguration.cycleCountEnabled : true;
  const returnNoteEnabled = processConfiguration ? processConfiguration.returnNoteEnabled : true;

  const { data: adjustments = [], isLoading: loadingAdj } = useQuery({
    queryKey: ['reports', 'stock-adjustment-register'],
    queryFn: async () => (await apiClient.get<StockAdjustmentRegisterRow[]>('/reports/stock-adjustment-register')).data,
  });
  const { data: cycleCounts = [], isLoading: loadingCycle } = useQuery({
    queryKey: ['reports', 'cycle-count-variance'],
    queryFn: async () => (await apiClient.get<CycleCountVarianceRow[]>('/reports/cycle-count-variance')).data,
  });
  const { data: returns = [], isLoading: loadingReturns } = useQuery({
    queryKey: ['reports', 'return-note-register'],
    queryFn: async () => (await apiClient.get<ReturnNoteRegisterRow[]>('/reports/return-note-register')).data,
  });

  const totalAdjustments = useMemo(() => new Set(adjustments.map((r) => r.adjustmentId)).size, [adjustments]);
  const totalVariance = useMemo(() => adjustments.reduce((sum, r) => sum + Math.abs(r.variance), 0), [adjustments]);
  const totalCycleCounts = useMemo(() => new Set(cycleCounts.map((r) => r.cycleCountId)).size, [cycleCounts]);
  const totalReturns = useMemo(() => new Set(returns.map((r) => r.returnNoteId)).size, [returns]);

  const reasonDistribution = useMemo(() => groupCountBy(adjustments, (r) => r.reason), [adjustments]);
  const returnTypeDistribution = useMemo(() => groupCountBy(returns, (r) => r.returnType), [returns]);
  const monthlyTrend = useMemo(() => groupSumByMonth(adjustments, (r) => r.adjustmentDate, [{ key: 'count', value: () => 1 }]), [adjustments]);

  const cycleColumns: DataTableColumn<ReturnType<typeof withId<CycleCountVarianceRow>>[number]>[] = [
    { field: 'cycleCountNumber', headerName: tr('columns.number') },
    { field: 'materialCode', headerName: tr('columns.materialCode') },
    { field: 'countDate', headerName: tr('columns.date'), render: (r) => formatDate(r.countDate) },
    { field: 'systemQty', headerName: t('adjustments.systemQty'), align: 'right' },
    { field: 'countedQty', headerName: t('adjustments.countedQty'), align: 'right', render: (r) => r.countedQty ?? '—' },
    { field: 'variance', headerName: tr('columns.variance'), align: 'right', render: (r) => r.variance ?? '—' },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        {stockAdjustmentEnabled && (
          <>
            <GridItem xs={12} sm={6} md={3}>
              <StatCard label={t('adjustments.totalAdjustments')} value={totalAdjustments} icon={<TuneIcon />} accentColor={colors.adjustments} />
            </GridItem>
            <GridItem xs={12} sm={6} md={3}>
              <StatCard label={t('adjustments.totalVariance')} value={totalVariance.toLocaleString()} icon={<SwapVertIcon />} accentColor={colors.adjustments} />
            </GridItem>
          </>
        )}
        {cycleCountEnabled && (
          <GridItem xs={12} sm={6} md={3}>
            <StatCard label={t('adjustments.totalCycleCounts')} value={totalCycleCounts} icon={<FactCheckIcon />} accentColor={colors.adjustments} />
          </GridItem>
        )}
        {returnNoteEnabled && (
          <GridItem xs={12} sm={6} md={3}>
            <StatCard label={t('adjustments.totalReturns')} value={totalReturns} icon={<KeyboardReturnIcon />} accentColor={colors.adjustments} />
          </GridItem>
        )}
      </GridContainer>

      {(stockAdjustmentEnabled || returnNoteEnabled) && (
        <GridContainer columns={12} spacing={2}>
          {stockAdjustmentEnabled && (
            <GridItem xs={12} md={returnNoteEnabled ? 6 : 12}>
              <ChartCard title={t('adjustments.reasonTitle')} loading={loadingAdj} empty={!loadingAdj && reasonDistribution.length === 0}>
                <DashboardBarChart
                  layout="horizontal"
                  categories={reasonDistribution.map((r) => r.key)}
                  series={[{ label: t('adjustments.count'), data: reasonDistribution.map((r) => r.count), color: colors.adjustments }]}
                />
              </ChartCard>
            </GridItem>
          )}
          {returnNoteEnabled && (
            <GridItem xs={12} md={stockAdjustmentEnabled ? 6 : 12}>
              <ChartCard title={t('adjustments.returnTypeTitle')} loading={loadingReturns} empty={!loadingReturns && returnTypeDistribution.length === 0}>
                <DashboardPieChart data={returnTypeDistribution.map((r, i) => ({ label: r.key, value: r.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))} />
              </ChartCard>
            </GridItem>
          )}
        </GridContainer>
      )}

      {stockAdjustmentEnabled && (
        <ChartCard title={t('adjustments.trendTitle')} loading={loadingAdj} empty={!loadingAdj && monthlyTrend.length === 0} height={260}>
          <DashboardLineChart
            categories={monthlyTrend.map((m) => m.month)}
            series={[{ label: t('adjustments.totalAdjustments'), data: monthlyTrend.map((m) => m.values.count), color: colors.adjustments, area: true }]}
            height={260}
          />
        </ChartCard>
      )}

      {cycleCountEnabled && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            {t('adjustments.cycleCountTableTitle')}
          </Typography>
          <DataTable columns={cycleColumns} rows={withId(cycleCounts, (r) => `${r.cycleCountId}-${r.materialCode}-${r.locationCode ?? ''}-${r.batchNo ?? ''}`)} loading={loadingCycle} exportable={false} defaultRowsPerPage={5} rowsPerPageOptions={[5, 10, 25]} />
        </Paper>
      )}
    </Stack>
  );
}
